import { useState, useEffect, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import axios from 'axios';

interface HistoryItem {
  id: string;
  rfpId: string;
  rfpName: string;
  status: 'in_progress' | 'completed' | 'failed';
  errorMessage?: string;
  progressPercentage: number;
  startedAt: string;
  completedAt?: string;
  createdAt: string;
  sectionsCount: number;
  totalCost?: number;
  duration?: number;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface ProposalProgress {
  rfpId: string;
  status: 'parsing' | 'analyzing' | 'generating' | 'completed' | 'error';
  progress: number;
  currentStep: string;
  error?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export function useProposalHistory(statusFilter?: string) {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);

  // Fetch history data
  const fetchHistory = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
      });

      if (statusFilter) {
        params.append('status', statusFilter);
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/rfp/history?${params}`,
        { withCredentials: true }
      );

      setHistory(response.data.history);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('Failed to fetch history:', err);
      setError('Failed to load proposal history');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, pagination.limit]);

  // Delete proposal
  const deleteProposal = useCallback(async (proposalId: string) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/rfp/history/${proposalId}`,
        { withCredentials: true }
      );

      // Remove from local state
      setHistory(prev => prev.filter(item => item.id !== proposalId));

      // Update pagination total
      setPagination(prev => ({
        ...prev,
        total: prev.total - 1,
        totalPages: Math.ceil((prev.total - 1) / prev.limit),
      }));
    } catch (err) {
      console.error('Failed to delete proposal:', err);
      throw new Error('Failed to delete proposal');
    }
  }, []);

  // Retry failed proposal
  const retryProposal = useCallback(async (rfpId: string) => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/rfp/generate/${rfpId}`,
        { useQuestionMode: true },
        { withCredentials: true }
      );

      // Refresh history to show new in-progress item
      await fetchHistory(pagination.page);
    } catch (err) {
      console.error('Failed to retry proposal:', err);
      throw new Error('Failed to retry proposal generation');
    }
  }, [fetchHistory, pagination.page]);

  // Setup Socket.IO connection
  useEffect(() => {
    const newSocket = io(API_BASE_URL);
    setSocket(newSocket);

    // Listen for proposal progress updates
    newSocket.on('proposal-progress', (data: ProposalProgress) => {
      setHistory(prev =>
        prev.map(item => {
          if (item.rfpId === data.rfpId && item.status === 'in_progress') {
            return {
              ...item,
              progressPercentage: data.progress,
            };
          }
          return item;
        })
      );
    });

    // Listen for RFP progress (question mode)
    newSocket.on('rfp-progress', (data: { rfpId: string; progress: number; status: string }) => {
      setHistory(prev =>
        prev.map(item => {
          if (item.rfpId === data.rfpId && item.status === 'in_progress') {
            return {
              ...item,
              progressPercentage: data.progress,
            };
          }
          return item;
        })
      );

      // If completed or error, refresh to get final state
      if (data.status === 'completed' || data.status === 'error') {
        setTimeout(() => fetchHistory(pagination.page), 1000);
      }
    });

    // Listen for completion
    newSocket.on('proposal-complete', () => {
      // Refresh history to update status
      fetchHistory(pagination.page);
    });

    return () => {
      newSocket.close();
    };
  }, [fetchHistory, pagination.page]);

  // Initial fetch and filter changes
  useEffect(() => {
    fetchHistory(1);
  }, [statusFilter, fetchHistory]); // Re-fetch when filter changes

  return {
    history,
    pagination,
    loading,
    error,
    socket,
    fetchHistory,
    deleteProposal,
    retryProposal,
  };
}
